var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createRemoteProvider, FlagsClient } from '@medable-platform/flags';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'libs.nucleus';
import { AuthContext } from 'libs.react_internal';
export const FeatureFlagsContext = createContext({
    isFeatureEnabled: () => false,
    areFlagsInitialized: false,
    getAllFeatureFlags: () => ({}),
    refreshFlags: () => undefined,
});
export const FeatureFlagsProvider = ({ children }) => {
    const { entityId, isLoggedIn } = useContext(AuthContext);
    const [areFlagsInitialized, setAreFlagsInitialized] = useState(false);
    const [client, setClient] = useState(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const refreshFlags = useCallback(() => {
        setAreFlagsInitialized(false);
        setRefreshNonce((nonce) => nonce + 1);
    }, []);
    useEffect(() => {
        if (isLoggedIn && entityId) {
            setAreFlagsInitialized(false);
            const flagProvider = createRemoteProvider(`${process.env.MOM_SERVICE_ENDPOINT}/v1/entities/${entityId}/flags`);
            const newClient = new FlagsClient(flagProvider);
            setClient((prevClient) => {
                if (prevClient) {
                    prevClient.destroy();
                }
                return newClient;
            });
        }
    }, [entityId, isLoggedIn, refreshNonce]);
    useEffect(() => {
        let cancelled = false;
        const initializeFlags = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!client) {
                return;
            }
            try {
                yield client.initialize();
            }
            catch (error) {
                console.error('Error initializing flags', error);
            }
            finally {
                if (!cancelled) {
                    setAreFlagsInitialized(true);
                }
            }
        });
        initializeFlags();
        return () => {
            cancelled = true;
        };
    }, [client]);
    const isFeatureEnabled = useCallback((flagName, context) => {
        if (!areFlagsInitialized || !client) {
            return false;
        }
        return client.isEnabled(flagName, context);
    }, [areFlagsInitialized, client]);
    const getAllFeatureFlags = useCallback(() => {
        if (!areFlagsInitialized || !client) {
            return {};
        }
        return client.getAllFlags();
    }, [areFlagsInitialized, client]);
    const providerValue = useMemo(() => {
        return {
            isFeatureEnabled,
            areFlagsInitialized,
            getAllFeatureFlags,
            refreshFlags,
        };
    }, [areFlagsInitialized, isFeatureEnabled, getAllFeatureFlags, refreshFlags]);
    return !isLoggedIn || areFlagsInitialized ? (_jsx(FeatureFlagsContext.Provider, { value: providerValue, children: children })) : (_jsx(Spinner, { wrapper: 'full' }));
};
